/**
 * src/services/feedbackService.js
 *
 * Client-side Firestore service for user feedback, ratings, and popup scheduling.
 * Works on Firebase Spark plan,  no Cloud Functions required.
 *
 * Collections:
 *  - admin_feedback: User feedback & ratings
 *  - user_popups: Scheduled/targeted popups per user
 */

import { db } from "../lib/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "../lib/firestoreClient";
import { sanitizeText, sanitizeUrl } from "../utils/sanitize";

const COLLECTIONS = {
  ADMIN_FEEDBACK: "admin_feedback",
  USER_POPUPS: "user_popups",
};

// ─── Feedback Submission ────────────────────────────────────────

/**
 * Submit user feedback (rating + message).
 * Reuses admin_feedback collection for admin visibility.
 */
export async function submitFeedback(user, {
  adminNotificationId = null,
  category = "general",
  topic = "",
  message = "",
  rating = null,
  ipAddress = null,
  source = "manual",
  metadata = {},
}) {
  if (!user) throw new Error("Authentication required");
  const uid = user.uid;
  const safeMessage = sanitizeText(message, 2000);
  const safeTopic = sanitizeText(topic, 120);
  const safeCategory = sanitizeText(category, 40) || "general";
  const safeSource = sanitizeText(source, 40) || "manual";
  if (!safeMessage && !rating) throw new Error("Message or rating required");

  const docRef = await addDoc(collection(db, COLLECTIONS.ADMIN_FEEDBACK), {
    userId: user.uid,
    userEmail: user.email,
    displayName: user.displayName || "",
    adminNotificationId,
    category: safeCategory,
    topic: safeTopic,
    message: safeMessage,
    rating,
    ipAddress: ipAddress || null,
    source: safeSource,
    status: "new",
    metadata,
    createdAt: serverTimestamp(),
  });

  return { id: docRef.id };
}

/**
 * Submit an error report from user.
 */
export async function submitErrorReport(user, {
  errorMessage = "",
  errorStack = "",
  userDescription = "",
  pageUrl = "",
   screenshot = null,
}) {
  if (!user) throw new Error("Authentication required");
  const uid = user.uid;

  const safeUserDescription = sanitizeText(userDescription, 2000);
  const safeErrorMessage = sanitizeText(errorMessage, 500);
  const safeErrorStack = sanitizeText(errorStack, 2000);
  const safePageUrl = sanitizeUrl(pageUrl || window.location.href);

  const docRef = await addDoc(collection(db, COLLECTIONS.ADMIN_FEEDBACK), {
    userId: uid,
    userEmail: user.email,
    displayName: user.displayName || "",
    category: "bug_report",
    message: safeUserDescription || "Error reported by user",
    rating: null,
    source: "error_report",
    status: "new",
    metadata: {
      errorMessage: safeErrorMessage,
      errorStack: safeErrorStack,
      pageUrl: safePageUrl,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
    createdAt: serverTimestamp(),
  });

  return { id: docRef.id };
}

// ─── User Popup Management ──────────────────────────────────────

/**
 * Get pending popups for the current user (unseen, scheduled in the past).
 */
export async function getPendingPopups(userId) {
  if (!userId) return [];

  const now = new Date();
  const q = query(
    collection(db, COLLECTIONS.USER_POPUPS),
    where("userId", "==", userId),
    where("seen", "==", false),
    orderBy("scheduledFor", "asc"),
    limit(5)
  );

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => {
      const scheduled = p.scheduledFor?.toDate?.() || new Date(p.scheduledFor);
      return scheduled <= now;
    });
}

/**
 * Listen for pending popups (real-time).
 */
export function listenPendingPopups(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const q = query(
    collection(db, COLLECTIONS.USER_POPUPS),
    where("userId", "==", userId),
    where("seen", "==", false),
    orderBy("scheduledFor", "asc"),
    limit(5)
  );

  return onSnapshot(
    q,
    (snap) => {
      const now = new Date();
      const pending = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => {
          const scheduled = p.scheduledFor?.toDate?.() || new Date(p.scheduledFor);
          return scheduled <= now;
        });
      callback(pending);
    },
    () => callback([])
  );
}

/**
 * Mark a popup as seen/dismissed.
 */
export async function dismissPopup(popupId) {
  await updateDoc(doc(db, COLLECTIONS.USER_POPUPS, popupId), {
    seen: true,
    seenAt: serverTimestamp(),
  });
}

/**
 * Schedule a feedback/rating popup for the current user.
 * Called after 10 minutes of activity — schedules for 24 hours later.
 */
export async function scheduleFeedbackPopup(userId) {
  if (!userId) return;

  const existing = query(
    collection(db, COLLECTIONS.USER_POPUPS),
    where("userId", "==", userId),
    where("type", "==", "feedback_rating"),
    where("seen", "==", false),
    limit(1)
  );
  const snap = await getDocs(existing);
  if (!snap.empty) return; // Already scheduled

  const scheduledFor = new Date();
  scheduledFor.setHours(scheduledFor.getHours() + 24);

  await addDoc(collection(db, COLLECTIONS.USER_POPUPS), {
    userId: userId,
    type: "feedback_rating",
    title: "Rate your experience",
    message: "How has your Orrith experience been? Your feedback helps us improve.",
    seen: false,
    scheduledFor,
    createdAt: serverTimestamp(),
    source: "timed_trigger",
  });
}

// ─── Admin Popup Functions ──────────────────────────────────────

/**
 * Admin: Send a targeted popup to specific users.
 */
export async function sendTargetedPopup(adminUser, {
  userIds = [],
  title,
  message,
  type = "admin_message",
  actionLabel = "",
  actionUrl = "",
}) {
  if (!adminUser || !title || !message) throw new Error("Missing required fields");

  const results = [];
  for (const userId of userIds) {
    try {
      const ref = await addDoc(collection(db, COLLECTIONS.USER_POPUPS), {
        userId,
        type,
        title,
        message,
        actionLabel: actionLabel || "",
        actionUrl: actionUrl || "",
        seen: false,
        scheduledFor: new Date(), // Immediate
        createdAt: serverTimestamp(),
        sentBy: adminUser.uid,
        sentByEmail: adminUser.email,
        source: "admin_targeted",
      });
      results.push({ userId, id: ref.id, success: true });
    } catch (err) {
      results.push({ userId, success: false, error: err.message });
    }
  }
  return results;
}

/**
 * Admin: Send popup to all users (broadcast).
 * Fetches user IDs from users collection and sends individually.
 */
export async function sendBroadcastPopup(adminUser, {
  title,
  message,
  type = "admin_message",
  actionLabel = "",
  actionUrl = "",
  maxUsers = 500,
}) {
  const usersSnap = await getDocs(
    query(collection(db, "users"), limit(maxUsers))
  );
  const userIds = usersSnap.docs.map((d) => d.id);
  return sendTargetedPopup(adminUser, { userIds, title, message, type, actionLabel, actionUrl });
}

/**
 * Admin: Listen to all popups (for management view).
 */
export function listenAllPopups(callback, limitCount = 50) {
  const q = query(
    collection(db, COLLECTIONS.USER_POPUPS),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    () => callback([])
  );
}

/**
 * Admin: Delete a popup.
 */
export async function deletePopup(popupId) {
  await deleteDoc(doc(db, COLLECTIONS.USER_POPUPS, popupId));
}

/**
 * Get feedback stats (for admin dashboard).
 */
export async function getFeedbackStats() {
  const allQ = query(collection(db, COLLECTIONS.ADMIN_FEEDBACK));
  const allSnap = await getDocs(allQ);
  const items = allSnap.docs.map((d) => d.data());

  const stats = {
    total: items.length,
    new: items.filter((i) => i.status === "new").length,
    reviewed: items.filter((i) => i.status === "reviewed").length,
    actioned: items.filter((i) => i.status === "actioned").length,
    avgRating: 0,
    ratingCount: 0,
    bugReports: items.filter((i) => i.category === "bug_report").length,
  };

  const rated = items.filter((i) => i.rating != null && i.rating > 0);
  if (rated.length > 0) {
    stats.avgRating = +(rated.reduce((sum, i) => sum + i.rating, 0) / rated.length).toFixed(1);
    stats.ratingCount = rated.length;
  }

  return stats;
}

// Canonical aliases for consistent service naming patterns.
export const createFeedback = submitFeedback;
export const createErrorReport = submitErrorReport;
export const listPendingPopups = getPendingPopups;
export const listUserPopups = listenPendingPopups;
export const removePopup = deletePopup;
